/**
 * Reports — Referral partners + Conversion (Reports Phase A + window presets
 * + the conversion ledger move, founder-directed 2026-07-14).
 *
 * THREE-TAB SUB-NAV ("Referrals · Conversion · Funnel", the #539 preset-pill
 * idiom, ?tab= param): Referrals keeps the Phase A content below; Conversion
 * hosts the ConversionLedger (formerly the Workspace Conversion view — the old
 * /workspace?view=conversion deep-link redirects here); Funnel hosts the
 * weekly intake funnel (FunnelReport: inquiries in / converted / waiting /
 * turned away by week, by unit, county, and referrer, with a CSV download).
 * All tabs gate on `view_referral_reports` (the conversion endpoints re-gated
 * with the move; the funnel endpoint shares the floor).
 * The ledger's drill rows open the intake DRAWER, hosted here via ?item=
 * (the workspace deep-link pattern); the link affordance is keyed on
 * canViewWorkspace (capability-driven — board_observer holds view_workspace,
 * so their rows link and the drawer renders read-only because every write
 * affordance gates on manage_work_items).
 *
 * A trend of new referrals (a hand-rolled div-bar chart — no charting
 * dependency, per the repo's tile/list idiom) plus a partners table:
 * Partner (linked to their Directory entry) · this week · recent avg · window
 * total · conversion rate. Completed weeks are labeled by their Sunday end
 * date; the current in-progress week is marked "partial".
 *
 * WINDOW PRESETS (founder-directed, 2026-07-14 referral-history backfill to
 * 2026-01-23 made the old fixed 12-week view too narrow): a 4-pill control —
 * 1 month (4 wk) / 3 months (13 wk, DEFAULT) / 6 months (26 wk) / All time
 * (server-resolved from the tenant's earliest intake, `weeks=all`). 3 months
 * is the default because it's the nearest preset to the PRIOR fixed default
 * (12 weeks) — closer than 1 month (4 wk) by a wide margin. Switching presets
 * refetches (the weeks value is in the query key). The trend chart thins its
 * date-axis labels (and hides the per-bar count) once the window gets wide
 * (26+ weeks) so it stays legible; the partner table and partner-engagement
 * rollups already recompute over whatever window comes back — no separate
 * handling needed there.
 *
 * Gated `canViewReferralReports` (founder ruling 2026-07-14 — owner,
 * board_observer, data_steward, exec_support). The page-level PageShell
 * access gate renders the denial panel for anyone else. The Partner
 * engagement block below reads the OPERATING weekly endpoint, which stays on
 * the narrower `canViewReports` (owner + board_observer only) — that query
 * is separately gated so data_steward/exec_support never draw a 403; the
 * block also self-degrades to nothing when it has no operating rows to join
 * against (see PartnerEngagementBlock.tsx). It reads its OWN fixed weekly
 * window (OPERATING_WEEKS below) — a SEPARATE endpoint/repo with its own
 * `weeks` param, not shared with the referral preset control, so it is
 * deliberately NOT wired to the preset (verified: the two `weeks` params
 * belong to distinct routes/repos; there is no shared param to keep
 * consistent).
 */

import { type ReactNode, useState } from 'react';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canViewReferralReports, canViewReports, canViewWorkspace } from '@/lib/capabilities';
import {
  reportsApi,
  operatingMetricsApi,
  type ReferralWeeklyReportWire,
  type ReferralWeekWire,
  type ReferralPartnerRowWire,
  type ReferralSourceRef,
  type ReferralChannelWire,
  type ReferralDrillResponse,
  type ConversionDrillRowWire,
  type OperatingMetricsReportWire,
} from '@/lib/api';
import { PageShell } from '@/components/primitives/PageShell';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { ConversionLedger } from '@/components/reports/ConversionLedger';
import { WorkItemDrawer } from '@/components/workspace/WorkItemDrawer';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { PartnerEngagementBlock } from '@/components/reports/PartnerEngagementBlock';
import { FunnelReport } from '@/components/reports/FunnelReport';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';

/** The partner-engagement block's OWN fixed window — unrelated to the
 *  referral preset control below (see the file docstring). Unchanged from
 *  the prior single WEEKS constant's value. */
const OPERATING_WEEKS = 12;

type WindowPresetKey = '1m' | '3m' | '6m' | 'all';

interface WindowPreset {
  key: WindowPresetKey;
  label: string;
  weeks: number | 'all';
}

const WINDOW_PRESETS: readonly WindowPreset[] = [
  { key: '1m', label: '1 month', weeks: 4 },
  { key: '3m', label: '3 months', weeks: 13 },
  { key: '6m', label: '6 months', weeks: 26 },
  { key: 'all', label: 'All time', weeks: 'all' },
];

// 3 months (13 weeks) is the nearest preset to the prior fixed 12-week
// default — see the file docstring.
const DEFAULT_PRESET: WindowPresetKey = '3m';

/** Beyond this many weeks the trend chart thins its x-axis date labels and
 *  hides the per-bar count (bars get too narrow to carry either legibly). */
const CHART_DENSE_THRESHOLD = 26;
/** Roughly how many date labels to keep visible no matter how wide the
 *  window gets (the all-time / 260-week ceiling case). */
const CHART_TARGET_LABEL_COUNT = 13;

function formatWeekEnd(iso: string): string {
  // iso is a date-only YYYY-MM-DD (the Sunday end, UTC).
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatMonthYear(iso: string): string {
  // iso is a date-only YYYY-MM-DD (the week start, UTC).
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatPercent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function formatAvg(n: number): string {
  return n.toFixed(1);
}

/** The Partner cell — a Directory link for org/individual sources, plain text otherwise. */
function PartnerName({ source, tenantSlug }: { source: ReferralSourceRef; tenantSlug: string }): ReactNode {
  if (source.directory_id && source.kind === 'org') {
    return (
      <Link
        className="text-sage-800 font-medium hover:underline"
        to={`/t/${encodeURIComponent(tenantSlug)}/organizations/${encodeURIComponent(source.directory_id)}`}
      >
        {source.label}
      </Link>
    );
  }
  if (source.directory_id && source.kind === 'individual') {
    return (
      <Link
        className="text-sage-800 font-medium hover:underline"
        to={`/t/${encodeURIComponent(tenantSlug)}/individuals/${encodeURIComponent(source.directory_id)}`}
      >
        {source.label}
      </Link>
    );
  }
  return <span className="text-ink">{source.label}</span>;
}

/** The won portion of a bar (affirm register — a converted referral is exactly
 *  what "settled/won" means). The remainder is a muted forest tint so the two
 *  read as ONE bar with a highlighted won portion (not two rival colors). */
const CONVERTED_FILL = 'var(--color-affirm-deep)';
const REMAINDER_FILL = 'var(--color-sage-200)';

function TrendChart({
  buckets,
  allTimeLabel,
}: {
  buckets: ReferralWeekWire[];
  /** "All time · since Jan 2026" — set only for a resolved all-time window. */
  allTimeLabel?: string | null;
}): ReactNode {
  const max = buckets.reduce((m, b) => Math.max(m, b.new_referrals), 0);
  const weeks = buckets.length;
  // Dense windows (26+ weeks — the 6-month/all-time presets) get too narrow
  // for a legible per-bar count; the tooltip still carries the exact figures.
  const showCounts = weeks <= CHART_DENSE_THRESHOLD;
  // Thin the date-axis labels so a wide window (up to the 260-week
  // all-time ceiling) keeps roughly CHART_TARGET_LABEL_COUNT labels instead
  // of an illegible wall of text. The most recent (rightmost, possibly
  // partial) week always keeps its label — it's index (weeks - 1), and
  // (weeks - 1 - i) % labelEvery === 0 there for any labelEvery.
  const labelEvery = Math.max(1, Math.ceil(weeks / CHART_TARGET_LABEL_COUNT));
  return (
    <div data-testid="referral-trend" className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h2 className="font-display text-lg text-sage-800">New referrals by week</h2>
        <div className="flex items-center gap-3 text-[11px] text-ink-soft">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: CONVERTED_FILL }} /> Converted
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: REMAINDER_FILL }} /> Not yet converted
          </span>
          {allTimeLabel && (
            <span data-testid="referral-all-time-label" className="text-[12px] text-ink-soft">
              {allTimeLabel}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-end gap-1.5 h-44">
        {buckets.map((b) => {
          // Stacked split: converted (affirm) on the base, the not-yet-converted
          // remainder (neutral) on top. Both heights are a % of the same `max`
          // (the tallest week's new_referrals), so a bar's total height stays
          // proportional to new_referrals and its green base to conversions.
          // conversions is a cohort subset of that week's new_referrals, so the
          // remainder is always >= 0 (clamped defensively).
          // The bar's TOTAL height is a % of the cell (which is h-full inside
          // the h-44 row — a definite height, so the percentage resolves).
          // The two segments then split the bar by SHARE (% of the wrapper,
          // which now carries the definite totalPct height). Percentages
          // against an auto-height parent collapse to 0 — the exact bug that
          // flattened every bar to its 3px minHeight (2026-07-14).
          const totalPct = max === 0 ? 0 : Math.round((b.new_referrals / max) * 100);
          const remainder = Math.max(0, b.new_referrals - b.conversions);
          const convShare = b.new_referrals === 0 ? 0 : Math.round((b.conversions / b.new_referrals) * 100);
          const remShare = 100 - convShare;
          return (
            <div
              key={b.week_end}
              data-testid="referral-trend-bar"
              data-partial={b.is_partial ? 'true' : 'false'}
              className="flex-1 h-full flex flex-col justify-end items-center"
              title={`Week ending ${formatWeekEnd(b.week_end)}${b.is_partial ? ' (partial)' : ''}: ${b.new_referrals} new, ${b.conversions} converted`}
            >
              <span className="text-[11px] font-semibold text-ink tabular-nums mb-1">
                {showCounts && b.new_referrals > 0 ? b.new_referrals : ''}
              </span>
              {/* One bar, two segments. The partial (current) week keeps its
                  muted treatment via reduced opacity so it still reads as
                  incomplete. */}
              <div
                className="w-full flex flex-col justify-end"
                style={{ height: `${totalPct}%`, minHeight: b.new_referrals > 0 ? '3px' : '0', opacity: b.is_partial ? 0.5 : 1 }}
              >
                <div
                  data-testid="referral-trend-bar-remainder"
                  className="w-full rounded-t-tight"
                  style={{ height: `${remShare}%`, minHeight: remainder > 0 ? '3px' : '0', backgroundColor: REMAINDER_FILL }}
                />
                <div
                  data-testid="referral-trend-bar-converted"
                  className="w-full"
                  style={{ height: `${convShare}%`, minHeight: b.conversions > 0 ? '3px' : '0', backgroundColor: CONVERTED_FILL }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1.5 mt-1.5">
        {buckets.map((b, i) => {
          const showLabel = (weeks - 1 - i) % labelEvery === 0;
          return (
            <div
              key={b.week_end}
              data-testid="referral-trend-week-label"
              data-label-shown={showLabel ? 'true' : 'false'}
              className="flex-1 text-center text-[10px] leading-tight text-ink-soft"
            >
              {showLabel ? (
                <>
                  {formatWeekEnd(b.week_end)}
                  {b.is_partial && <span className="block text-sage-400 font-semibold">partial</span>}
                </>
              ) : (
                <span aria-hidden="true" className="block text-sage-300">
                  ·
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type ReportsTab = 'referrals' | 'conversion' | 'funnel';

/**
 * The Referrals · Conversion · Funnel sub-nav — the same second-level
 * pill-row idiom as WindowPresetControl below (#539; rounded-pill,
 * sage-100/700 active vs paper/ink-soft rest; role="tablist"/"tab").
 */
function ReportsTabNav({ active, onChange }: { active: ReportsTab; onChange: (t: ReportsTab) => void }): ReactNode {
  const TABS: { key: ReportsTab; label: string }[] = [
    { key: 'referrals', label: 'Referrals' },
    { key: 'conversion', label: 'Conversion' },
    { key: 'funnel', label: 'Funnel' },
  ];
  return (
    <div role="tablist" aria-label="Report section" data-testid="reports-tabs" className="flex flex-wrap items-center gap-2">
      {TABS.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`reports-tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className={
              'min-h-9 flex items-center px-3 py-1 text-sm font-medium rounded-pill transition-colors duration-[var(--dur-base)] ease-quiet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
              (isActive
                ? 'bg-sage-100 text-sage-700 border border-sage-300'
                : 'bg-paper text-ink-soft border border-sage-800/[8%] hover:border-sage-400/30')
            }
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The 1m/3m/6m/all-time window preset control — the second-level pill-row
 * idiom shared with AdminReviewTabs' view pills (rounded-pill, sage-100/700
 * active vs paper/ink-soft rest; role="tablist"/"tab" single-select).
 */
function WindowPresetControl({
  active,
  onChange,
}: {
  active: WindowPresetKey;
  onChange: (next: WindowPresetKey) => void;
}): ReactNode {
  return (
    <div
      role="tablist"
      aria-label="Report window"
      data-testid="referral-window-presets"
      className="flex flex-wrap items-center gap-2"
    >
      {WINDOW_PRESETS.map((p) => {
        const isActive = p.key === active;
        return (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`referral-window-preset-${p.key}`}
            onClick={() => onChange(p.key)}
            className={
              'min-h-9 flex items-center px-3 py-1 text-sm font-medium rounded-pill transition-colors duration-[var(--dur-base)] ease-quiet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
              (isActive
                ? 'bg-sage-100 text-sage-700 border border-sage-300'
                : 'bg-paper text-ink-soft border border-sage-800/[8%] hover:border-sage-400/30')
            }
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

/** The Referral category cell — the DOMINANT referrer_type + a quiet "(+N other)"
 *  when the partner's intakes carry several (surfacing tagging inconsistency);
 *  a dash when uncategorized. Sourced from the intake tracker ONLY (founder
 *  ruling 2026-07-15) — never the Directory taxonomy. */
function CategoryCell({ partner }: { partner: ReferralPartnerRowWire }): ReactNode {
  const other = partner.category_other_count ?? 0;
  if (!partner.category_label) return <span className="text-sage-300">–</span>;
  return (
    <span>
      {partner.category_label}
      {other > 0 && (
        <span
          className="ml-1 text-[11px] text-sage-400"
          data-testid="referral-partner-category-other"
          title={`This partner's intakes span ${other + 1} categories in the intake tracker`}
        >
          (+{other} other)
        </span>
      )}
    </span>
  );
}

function PartnersTable({
  partners,
  tenantSlug,
  weeks,
  completeWeeks,
  onDrillPartner,
  scopedChannel,
}: {
  partners: ReferralPartnerRowWire[];
  tenantSlug: string;
  weeks: number;
  completeWeeks: number;
  onDrillPartner: (partner: ReferralPartnerRowWire) => void;
  /** When a channel filter is active, its label — every per-row number is
   *  scoped to that channel's intakes (the header says so). */
  scopedChannel: string | null;
}): ReactNode {
  // The average is over COMPLETE weeks only (the server excludes the partial
  // current week), so the header names the actual divisor.
  const avgWeeks = Math.max(1, Math.min(4, completeWeeks));
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <h2 className="font-display text-lg text-sage-800">Referral partners</h2>
        {scopedChannel && (
          <span data-testid="referral-partners-scope-note" className="text-[13px] font-medium text-sage-700">
            Showing {scopedChannel} numbers for each partner
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table data-testid="referral-partners" className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]">
              <th className="py-2 pr-3">Partner</th>
              {/* Renamed from "Category" (founder ruling 2026-07-15): the column
                  is now the intake-tracker referral category (referrer_type)
                  ONLY, never the Directory taxonomy. */}
              <th className="py-2 px-3">Referral category</th>
              <th className="py-2 px-3 text-right">
                This week{' '}
                <span className="normal-case tracking-normal font-normal text-sage-400">(partial)</span>
              </th>
              <th className="py-2 px-3 text-right">{avgWeeks}-wk avg</th>
              <th className="py-2 px-3 text-right">{weeks}-wk total</th>
              {/* Relabeled from "Conversion rate" (founder ruling 2026-07-15):
                  that term is now reserved for the paperwork-received ÷
                  paperwork-sent ratio on the Conversion tab (ConversionLedger).
                  This column is a DIFFERENT ratio (converted ÷ referrals from
                  this partner, i.e. partner lead quality) — relabeled to avoid
                  two meanings of one term. Math + underlying field are
                  unchanged. */}
              <th className="py-2 pl-3 text-right" title="Converted ÷ referrals from this partner (partner lead quality)">Became clients</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              // The row opens the drill (the underlying intakes → the drawer,
              // where a mis-tagged referrer_type is fixed). The Partner cell's
              // Directory link stops propagation so it still navigates.
              <tr
                key={p.source.key}
                data-testid="referral-partner-row"
                role="button"
                tabIndex={0}
                aria-label={`View intakes referred by ${p.source.label}`}
                onClick={() => onDrillPartner(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onDrillPartner(p);
                  }
                }}
                className="border-b border-sage-800/[5%] last:border-0 cursor-pointer hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                {/* The partner-name link navigates on its own — stop BOTH click
                    and keydown from bubbling to the row so focusing the name and
                    pressing Enter navigates WITHOUT also firing the row drill. */}
                <td
                  className="py-2 pr-3"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <PartnerName source={p.source} tenantSlug={tenantSlug} />
                </td>
                <td className="py-2 px-3 text-ink-soft" data-testid="referral-partner-category">
                  <CategoryCell partner={p} />
                </td>
                <td className="py-2 px-3 text-right tabular-nums text-ink">{p.this_week}</td>
                <td className="py-2 px-3 text-right tabular-nums text-ink">{formatAvg(p.recent_avg)}</td>
                <td className="py-2 px-3 text-right tabular-nums text-ink font-semibold">{p.window_total}</td>
                <td className="py-2 pl-3 text-right tabular-nums text-ink">{formatPercent(p.conversion_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The channel filter chips — one per configured referrer_type value (the intake
 * tracker's category vocabulary, from config), plus an "All" reset. Each chip
 * carries its CHANNEL rollup (referrals + became-clients%) — doubling as the
 * channel-performance analytic (Attorney vs Web vs Medical). Clicking filters
 * the partner table to partners referring via that channel.
 *
 * Register law: the chips are WAYFINDING (sage selection hue — same pill idiom
 * as the window presets); the became-clients% is DATA, shown in quiet ink, not
 * a state color.
 */
function ChannelChips({
  channels,
  selected,
  onSelect,
}: {
  channels: ReferralChannelWire[];
  selected: string | null;
  onSelect: (value: string | null) => void;
}): ReactNode {
  if (channels.length === 0) return null;
  const chipClass = (isActive: boolean): string =>
    'min-h-9 inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded-pill transition-colors duration-[var(--dur-base)] ease-quiet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
    (isActive
      ? 'bg-sage-100 text-sage-700 border border-sage-300'
      : 'bg-paper text-ink-soft border border-sage-800/[8%] hover:border-sage-400/30');
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4" data-testid="referral-channels">
      <div className="flex items-baseline gap-2 mb-2.5">
        <h2 className="font-display text-lg text-sage-800">Referral channels</h2>
        <span className="text-[12px] text-ink-soft">how each category performs · click to filter</span>
      </div>
      <div role="group" aria-label="Referral channel filter" className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="referral-channel-all"
          aria-pressed={selected === null}
          onClick={() => onSelect(null)}
          className={chipClass(selected === null)}
        >
          All
        </button>
        {channels.map((c) => {
          const isActive = selected === c.value;
          return (
            <button
              key={c.value}
              type="button"
              data-testid="referral-channel-chip"
              data-channel={c.value}
              aria-pressed={isActive}
              onClick={() => onSelect(isActive ? null : c.value)}
              className={chipClass(isActive)}
            >
              <span className="font-medium">{c.label}</span>
              <span className="tabular-nums text-ink-soft">{c.referrals}</span>
              <span className="tabular-nums text-sage-400">· {formatPercent(c.conversion_rate)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DRILL_DISPOSITION: Record<ConversionDrillRowWire['disposition'], { text: string; cls: string }> = {
  converted: { text: 'Won', cls: 'text-affirm-deep bg-tick-affirm' },
  closed_unconverted: { text: 'Lost', cls: 'text-pepper bg-pepper/5' },
  in_progress: { text: 'Still deciding', cls: 'text-sage-400 bg-sage-50' },
};

/**
 * The referral drill panel — a modal list of the intakes behind a clicked
 * partner (source:<key>) or channel (referrer_type:<value>). Its count is
 * server-guaranteed to equal the clicked number (the trust contract); each row
 * opens the intake drawer (the same ?item= path the conversion drill uses) when
 * the host passes onOpen (capability-driven). The drawer is read-only for a
 * board_observer (every write affordance gates on manage_work_items).
 */
function ReferralDrillPanel({
  slug,
  weeks,
  segment,
  label,
  onClose,
  onOpen,
}: {
  slug: string;
  weeks: number | 'all';
  segment: string;
  label: string;
  onClose: () => void;
  onOpen?: (id: string) => void;
}): ReactNode {
  const query = useQuery<ReferralDrillResponse>({
    queryKey: ['reports-referrals-drill', slug, weeks, segment],
    queryFn: ({ signal }) => reportsApi.referralsDrill(slug, { weeks, segment }, signal),
    staleTime: 60_000,
  });
  const drill = query.data ?? null;
  return (
    <OverlayPortal>
      <div
        className="fixed inset-0 z-30 flex items-center justify-center bg-sage-800/20 p-4"
        role="dialog"
        aria-modal="true"
        aria-label={`Intakes: ${label}`}
        data-testid="referral-drill-panel"
        onClick={onClose}
      >
        <div className="bg-paper rounded-card shadow-sh2 w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-baseline justify-between gap-3 px-5 py-3.5 border-b border-sage-800/[8%]">
            <div>
              <h2 className="font-display text-base text-sage-800">{label}</h2>
              {drill && (
                <p className="text-[12px] text-sage-400 mt-0.5" data-testid="referral-drill-count">
                  {drill.count} {drill.count === 1 ? 'intake' : 'intakes'}
                </p>
              )}
            </div>
            <button
              type="button"
              data-testid="referral-drill-close"
              onClick={onClose}
              className="text-sage-400 hover:text-sage-800 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight px-1"
            >
              Close
            </button>
          </div>
          <div className="overflow-y-auto px-2 py-2">
            {query.isPending ? (
              <div className="px-3"><SkeletonRow /><SkeletonRow /></div>
            ) : query.isError ? (
              <div className="px-3 py-2"><ErrorBanner error={query.error} onRetry={() => void query.refetch()} what="the report" /></div>
            ) : !drill || drill.rows.length === 0 ? (
              <p className="text-sm text-ink-soft px-3 py-4" data-testid="referral-drill-empty">This segment is empty for these dates.</p>
            ) : (
              <ul data-testid="referral-drill-list">
                {drill.rows.map((r) => {
                  const disp = DRILL_DISPOSITION[r.disposition];
                  return (
                    <li key={r.work_item_id}>
                      <button
                        type="button"
                        data-testid="referral-drill-row"
                        onClick={() => onOpen?.(r.work_item_id)}
                        className="w-full text-left rounded-tight px-3 py-2 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="text-[13px] font-medium text-ink truncate">{r.display_name ?? 'Unnamed intake'}</span>
                          <span className={`ml-auto flex-shrink-0 text-[10px] font-semibold rounded-pill px-1.5 py-0.5 ${disp.cls}`}>{disp.text}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 text-[11.5px] text-sage-400 mt-0.5">
                          {r.current_stage_label && <span>{r.current_stage_label}</span>}
                          {r.contact_name && <span>· {r.contact_name}</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

export function Reports(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  // Referral analytics (founder ruling 2026-07-14) — owner, board_observer,
  // data_steward, exec_support. The operating dashboard stays the narrower
  // owner/board_observer-only tier; the page is reachable by anyone who
  // holds either (in practice canOperating is always a subset of
  // canReferrals — see the capability matrix — so `allowed` === canReferrals
  // today, written as an OR for robustness against future divergence).
  const canReferrals = canViewReferralReports(whoami);
  const canOperating = canViewReports(whoami);
  const allowed = canReferrals || canOperating;
  // The drawer link affordance is capability-driven: every conversion viewer
  // today holds view_workspace (incl. board_observer — read-only inside, as
  // the drawer's writes gate on manage_work_items).
  const canWorkspace = canViewWorkspace(whoami);

  // ?tab= selects the section (referrals default); ?item= deep-links the
  // intake drawer (the workspace pattern — the conversion redirect preserves it).
  const rawTab = searchParams.get('tab');
  const tab: ReportsTab = rawTab === 'conversion' || rawTab === 'funnel' ? rawTab : 'referrals';
  const selectedItemId = searchParams.get('item');
  function setTab(next: ReportsTab): void {
    const sp = new URLSearchParams(searchParams);
    if (next === 'referrals') sp.delete('tab');
    else sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  }
  function openItem(id: string): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('item', id);
    setSearchParams(sp);
  }
  function closeItem(): void {
    const sp = new URLSearchParams(searchParams);
    sp.delete('item');
    setSearchParams(sp, { replace: true });
  }

  const [preset, setPreset] = useState<WindowPresetKey>(DEFAULT_PRESET);
  const activePreset = WINDOW_PRESETS.find((p) => p.key === preset) ?? WINDOW_PRESETS[1]!;
  // Channel filter (a configured referrer_type value) + the referral drill
  // target. Switching the window resets both (their counts no longer apply).
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [referralDrill, setReferralDrill] = useState<{ segment: string; label: string } | null>(null);
  function changePreset(next: WindowPresetKey): void {
    setPreset(next);
    setSelectedChannel(null);
    setReferralDrill(null);
  }

  const query = useQuery<ReferralWeeklyReportWire>({
    queryKey: ['reports-referrals-weekly', tenantSlug, activePreset.weeks],
    queryFn: ({ signal }) => reportsApi.referralsWeekly(tenantSlug, { weeks: activePreset.weeks }, signal),
    staleTime: 60_000,
    enabled: canReferrals,
  });

  // Partner-engagement block inputs (build-specs/partner-engagement-analytics.md):
  // the operating weekly rows carry `sections.partners` (weekly touches + pies);
  // tenant config carries the partner-alias map. Gated on `canOperating`
  // (view_reports), NOT the broader `allowed` — the operating endpoint stays
  // owner/board_observer only, so a referral-only viewer (data_steward,
  // exec_support) never calls it (no 403 noise) and the block below never
  // renders for them. The referral report above is reused for the referral
  // half of the join (per-partner rollups + weekly buckets). Its window is
  // OPERATING_WEEKS — fixed, NOT the referral preset (see file docstring).
  const operatingQuery = useQuery<OperatingMetricsReportWire>({
    queryKey: ['reports-operating-weekly', tenantSlug, OPERATING_WEEKS],
    queryFn: ({ signal }) => operatingMetricsApi.weekly(tenantSlug, { weeks: OPERATING_WEEKS }, signal),
    staleTime: 60_000,
    enabled: canOperating,
  });
  const { data: tenantConfig } = useTenantConfig(tenantSlug, { enabled: canOperating });

  const data = query.data;
  const isEmpty = !!data && data.partners.length === 0;
  const operatingRows = operatingQuery.data?.rows ?? [];
  const allTimeLabel =
    data && data.is_all_time && data.buckets.length > 0
      ? `All time · since ${formatMonthYear(data.buckets[0]!.week_start)}`
      : null;

  // Header copy follows the active tab; the shell's chrome (eyebrow, gate,
  // width) is shared. The referral tab's primary query renders through an
  // INLINE AsyncBoundary (not the shell's async prop) so the tab nav stays
  // reachable while the referral report loads or errors.
  const header =
    tab === 'conversion'
      ? {
          title: 'Conversion',
          actions: undefined,
        }
      : tab === 'funnel'
      ? {
          title: 'Intake funnel',
          actions: undefined,
        }
      : {
          title: 'Referral partners',
          actions: <WindowPresetControl active={preset} onChange={changePreset} />,
        };

  // Channel chips come from the UNSCOPED report. When a channel is selected we
  // fetch a CHANNEL-SCOPED report (referrer_type filter) so each partner row's
  // numbers (referrals, this-week, became-clients%) reflect ONLY that channel's
  // intakes for that partner — the honest, useful "mistagged subset" view
  // (founder review 2026-07-15), not the full-window all-category number.
  const channels: ReferralChannelWire[] = data?.channels ?? [];
  const allPartners = data?.partners ?? [];
  const scopedQuery = useQuery<ReferralWeeklyReportWire>({
    queryKey: ['reports-referrals-weekly', tenantSlug, activePreset.weeks, selectedChannel],
    queryFn: ({ signal }) =>
      reportsApi.referralsWeekly(tenantSlug, { weeks: activePreset.weeks, referrerType: selectedChannel }, signal),
    staleTime: 60_000,
    enabled: canReferrals && selectedChannel !== null,
  });
  const tablePartners = selectedChannel === null ? allPartners : scopedQuery.data?.partners ?? [];
  const scopedLoading = selectedChannel !== null && scopedQuery.isPending;

  return (
    <PageShell
      testId="reports-main"
      maxWidth="wide"
      eyebrow="Olive · Reports"
      title={header.title}
      actions={header.actions}
      access={{ allowed }}
    >
      <ReportsTabNav active={tab} onChange={setTab} />
      {tab === 'funnel' ? (
        <FunnelReport tenantSlug={tenantSlug} enabled={canReferrals} />
      ) : tab === 'conversion' ? (
        // The conversion ledger owns its period + fetch. Drill rows open the
        // intake drawer when the viewer holds view_workspace (capability-
        // driven; no no-link fallback — every conversion viewer holds it).
        <ConversionLedger onOpen={canWorkspace ? openItem : undefined} />
      ) : (
        <AsyncBoundary
          isLoading={canReferrals && query.isPending}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={isEmpty}
          emptyHeadline="Once you record intakes, you'll see their referral sources here."
        >
          {data && !isEmpty ? (
            <div className="space-y-6">
              <TrendChart buckets={data.buckets} allTimeLabel={allTimeLabel} />
              <ChannelChips channels={channels} selected={selectedChannel} onSelect={(v) => { setSelectedChannel(v); setReferralDrill(null); }} />
              {selectedChannel && (
                <div className="-mt-3">
                  <button
                    type="button"
                    data-testid="referral-channel-drill"
                    onClick={() =>
                      setReferralDrill({ segment: `referrer_type:${selectedChannel}`, label: `Intakes via ${selectedChannel}` })
                    }
                    className="text-[13px] font-semibold text-sage-700 hover:text-sage-800 rounded-tight px-1 py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                  >
                    View all {selectedChannel} intakes
                  </button>
                </div>
              )}
              {scopedLoading ? (
                <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4" data-testid="referral-partners-loading">
                  <SkeletonRow />
                  <SkeletonRow />
                </div>
              ) : tablePartners.length === 0 ? (
                <p data-testid="referral-partners-empty-channel" className="text-sm text-ink-soft px-1">
                  No partners referred via {selectedChannel} in this window.
                </p>
              ) : (
                <PartnersTable
                  partners={tablePartners}
                  tenantSlug={tenantSlug}
                  weeks={data.weeks}
                  completeWeeks={data.buckets.filter((b) => !b.is_partial).length}
                  scopedChannel={selectedChannel}
                  onDrillPartner={(p) =>
                    setReferralDrill({ segment: `source:${p.source.key}`, label: `Intakes referred by ${p.source.label}` })
                  }
                />
              )}
              <p data-testid="referral-week-footnote" className="text-[12px] text-ink-soft">
                Weeks close before midnight on Sunday, so an entry recorded late Sunday evening may
                count toward the following week.
              </p>
              {canOperating && (
                <PartnerEngagementBlock
                  operatingRows={operatingRows}
                  referralPartners={data.partners}
                  referralBuckets={data.buckets}
                  config={tenantConfig?.config}
                />
              )}
            </div>
          ) : null}
        </AsyncBoundary>
      )}
      {/* The referral drill panel — a partner row / channel chip opens the list
          of the underlying intakes; each opens the drawer (?item=) to fix a
          mis-tagged referrer_type. onOpen is capability-driven (view_workspace
          holders; board_observer's drawer is read-only). */}
      {tab === 'referrals' && referralDrill && (
        <ReferralDrillPanel
          slug={tenantSlug}
          weeks={activePreset.weeks}
          segment={referralDrill.segment}
          label={referralDrill.label}
          onClose={() => setReferralDrill(null)}
          onOpen={canWorkspace ? openItem : undefined}
        />
      )}
      {/* The intake drawer — the drill rows' click-through (?item=), the same
          deep-link pattern the workspace uses. Renders read-only for a
          board_observer (writes gate on manage_work_items inside). */}
      {selectedItemId && canWorkspace && (
        <WorkItemDrawer key={selectedItemId} itemId={selectedItemId} onClose={closeItem} />
      )}
    </PageShell>
  );
}
